// ─────────────────────────────────────────────
//  roomStatusPieces — the shared visual language of the mobile Room Status
//  screens (design: docs/specs/2026-09-07-mobile-room-status-design.md §6).
//
//  PRESENTATIONAL ONLY. Nothing here imports Firestore, a hook or a context —
//  every value arrives as a prop, exactly like breakPieces. The MobileRoomCard
//  rule applies verbatim: this file renders and delegates.
//
//  ── ONE ROW, TWO MODES (§6.1) ──
//  Both modes always render all three columns (phase · Time · Ack); only WHICH
//  CELL IS INTERACTIVE changes. Provider: the phase cell is a <button>, the ack
//  cell is static. Supervisor: the phase cell is static, the ack cell is the
//  control. `mode = null` is read-only. No branch above the row and no second
//  component, so the two screens cannot drift about what a phase means.
//
//  ── "SEEN", NEVER "PRESENT" (§12) ──
//  An acknowledgement is a receipt (D4). A persisted ackAt eleven minutes after
//  induction is a QUERYABLE RECORD, and under 42 CFR §415.110 medical direction
//  it must not read as an attestation that the attending was present at
//  induction — it is not one, and the anesthesia record says otherwise.
//
//  ── PHASE STALENESS (§12, the adopted critical) ──
//  "Induction 07:40" still reads *Induction* at 14:00 with total confidence,
//  and a confidently-wrong board is the stated fear of this design (§1). So a
//  stamped row shows its AGE, and past a plausible interval it STOPS ASSERTING
//  the phase: the word STALE, the live emphasis dropped, and the same fact in
//  its aria-label. The recorded time stays — the stamp really happened — but
//  the row no longer claims the room is in that phase now.
//
//  ── NEVER COLOUR ALONE ──
//  Every state carries a WORD and, where it claims completion, a SHAPE:
//  "—" · "AWAITING" · "✓ SEEN 07:44 · Name". Colour is the third signal.
// ─────────────────────────────────────────────
import React from "react";

import mobile, { px, INPUT_FONT_SIZE } from "../../theme/tokens.mobile";
import { formatClock } from "../../domain/stampTime";
import {
  CASE_PHASES,
  CASE_EVENTS,
  CASE_EVENT_META,
  phaseLabel,
  phaseState,
} from "../../domain/casePhase";

const { color, space, radius } = mobile;

// ⚠️ 48, NEVER 44. 44 is the AUDIT FLOOR, not a budget: layout engines floor
// lengths to 1/64px, so an element budgeted at exactly 44 renders 43.98 and
// fails the very check it was sized for. Budget the minimum plus slack.
export const TOUCH_TARGET_MIN = 48;

// ── Time + age formatting ────────────────────────────────────────────────────
// stampedAt / ackAt arrive in whatever shape the wiring layer has: a Firestore
// Timestamp, a Date, epoch millis, an ISO string, or an already-formatted
// "HH:MM". Anything unreadable renders as the em dash — never "Invalid Date",
// never a silent blank.
export const EM_DASH = "—";

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "object" && typeof value.toDate === "function") {
    try {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }
  if (typeof value === "object" && Number.isFinite(value.seconds)) {
    return new Date(value.seconds * 1000);
  }
  if (typeof value === "number" && Number.isFinite(value)) return new Date(value);
  if (typeof value === "string") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

// ⚠️ THE COERCION MOVED TO domain/stampTime, and this delegates to it. The board
// card had its own copy that could not read a Firestore Timestamp, so the phone
// showed four phases stamped and the board showed the same four as never
// stamped — one document, two readers, two answers. One implementation now.
// The EM_DASH fallback stays HERE because it is this screen's rendering choice,
// not part of reading a time.
export function formatStampTime(value) {
  return formatClock(value) || EM_DASH;
}

// "12m ago" / "1h 20m ago". Returns "" for an absent or nonsensical age so the
// caller renders nothing rather than "NaN ago".
// ⚠️ Number.isFinite, not a truthiness test: Number(null) === 0 and a bare
// `if (ageMinutes)` would drop a genuine "0 minutes ago" on the floor.
export function formatAge(minutes) {
  if (!Number.isFinite(minutes) || minutes < 0) return "";
  const m = Math.floor(minutes);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h}h ${rest}m ago` : `${h}h ago`;
}

// How long each phase can plausibly still be TRUE before the row must stop
// asserting it. DISPLAY thresholds, not clinical claims: crossing one never
// changes a stamp or a board status. Generous on purpose — a row that cries
// stale too early trains people to ignore the word, which is exactly how the
// colour that means something also gets ignored.
export const PHASE_STALE_AFTER_MIN = {
  to_room: 45,
  induction: 90,
  closing: 180,
  emergence: 90,
};

export function isPhaseStale(phaseId, ageMinutes) {
  const limit = PHASE_STALE_AFTER_MIN[phaseId];
  if (!Number.isFinite(limit) || !Number.isFinite(ageMinutes)) return false;
  return ageMinutes > limit;
}

// Guarded matchMedia read — the same pattern DoctorsNeededBox / GoHomeAlert use.
// Motion here is decoration only (a press transition); every state is carried
// by a word and a shape, so suppressing it removes nothing (WCAG 2.3.3).
export function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
// ⚠️ minWidth: 0 is load-bearing on every cell: without it a long label refuses
// to shrink and pushes the ack control off a 390px screen. On a phone the
// answer is always a narrower column or a wrapped label, never a sideways drag.
const CELL = { display: "flex", flexDirection: "column", justifyContent: "center", minHeight: TOUCH_TARGET_MIN, minWidth: 0 };
const s = {
  table: { width: "100%", tableLayout: "fixed", borderCollapse: "collapse", marginTop: px(space.sm) },
  th: { textAlign: "left", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: color.text.muted, fontWeight: 700, padding: `0 ${px(6)} ${px(4)} 0`, borderBottom: `1px solid ${color.border.subtle}` },
  td: { padding: `${px(2)} ${px(6)} ${px(2)} 0`, verticalAlign: "middle", borderBottom: `1px solid ${color.border.subtle}`, minWidth: 0 },
  phaseLabel: { ...CELL, flexDirection: "row", alignItems: "center", fontSize: 14, fontWeight: 700, color: color.text.primary, paddingLeft: px(8) },
  timeCell: CELL,
  ackCell: CELL,
  timeValue: { fontSize: 15, fontWeight: 700, color: color.text.primary },
  ageLine: { fontSize: 10, color: color.text.muted, lineHeight: 1.25 },
  ackWord: { fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", color: color.accent.gold },
  ackSeen: { fontSize: 11, fontWeight: 800, letterSpacing: "0.04em", color: color.break.done },
  ackName: { fontSize: 10, color: color.text.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  muted: { fontSize: 14, color: color.text.muted },
  staleWord: { fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", color: color.text.muted, border: `1px solid ${color.border.default}`, borderRadius: px(radius.sm), padding: `0 ${px(4)}`, alignSelf: "flex-start" },

  // ── The step number on a phase button ──────────────────────────────────────
  // These four happen in order, every case. Numbering them turns four similar
  // boxes into a sequence, which is what a CRNA is actually reporting: where in
  // the case we are. It is DECORATION — `aria-hidden`, because the accessible
  // name already says "Stamp Induction" and "1 Induction" would read worse.
  stepNum: {
    flex: "0 0 auto",
    width: px(22), height: px(22), borderRadius: px(11),
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    fontSize: 11, fontWeight: 800, lineHeight: 1,
  },
  // ⚠️ IT WRAPS, IT DOES NOT ELLIPSIS. This label was `nowrap` + ellipsis and
  // rendered "Induct…" and "Wakin…" on a 390px phone: the HINT below it is the
  // widest thing in the cell, so it set the column width and the name was then
  // cut to match. In a row where something has to give, the name is the only
  // shrinkable thing — and the name of the phase is the whole point of the row.
  // Two short words wrap onto two lines and stay readable; a truncated clinical
  // label does not.
  phaseText: { fontSize: 15, fontWeight: 800, minWidth: 0, lineHeight: 1.15 },
  // The affordance. A pending phase says what pressing it does; a stamped one
  // says how to correct it. Without this the four boxes look identical to a
  // clinician who has never used the screen.
  // ONE WORD. "TAP TO RECORD" wrapped to two lines and was wider than the phase
  // name, which is how it came to dictate the column width.
  phaseHint: { fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: color.text.muted, whiteSpace: "nowrap" },

  // ── Acknowledgement, as a pill ─────────────────────────────────────────────
  // A pill reads as a STATUS at a glance where bare text reads as a label. The
  // word inside is unchanged and still carries the meaning.
  ackPill: {
    display: "inline-flex", alignItems: "center", gap: px(4),
    padding: `${px(3)} ${px(8)}`, borderRadius: px(999),
    fontSize: 11, fontWeight: 800, letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },
  timeBig: { fontSize: 17, fontWeight: 800, color: color.text.primary, fontVariantNumeric: "tabular-nums" },
};

// A phase button, styled by its state. Three visual states, and the WORDS in
// the row still carry the meaning — this is the second signal, never the first.
//
// ⚠️ PENDING IS DASHED, STAMPED IS FILLED. A solid box that does nothing when
// pressed and a solid box that has already been pressed looked the same, so the
// grid gave no sense of progress through a case. Dashed = nothing recorded yet.
const phaseButton = (state, stale, reduce) => {
  const done = state === "acked";
  const stamped = state === "stamped";
  const accent = done ? color.break.done : stale ? color.border.default : color.accent.gold;
  return {
    width: "100%", minHeight: TOUCH_TARGET_MIN, minWidth: TOUCH_TARGET_MIN,
    display: "flex", flexDirection: "row", alignItems: "center", gap: px(8),
    padding: `${px(10)} ${px(10)}`, borderRadius: px(radius.md ?? radius.sm),
    border: state === "pending" ? `1px dashed ${color.border.default}` : `1.5px solid ${accent}`,
    background: state === "pending" ? "transparent" : `${accent}1F`,
    color: color.text.primary, font: "inherit", textAlign: "left", cursor: "pointer",
    transition: reduce ? "none" : "background 120ms ease, border-color 120ms ease",
  };
};

const stepNumStyle = (state, stale) => {
  const done = state === "acked";
  const accent = done ? color.break.done : stale ? color.border.default : color.accent.gold;
  return state === "pending"
    ? { ...s.stepNum, border: `1px dashed ${color.border.default}`, color: color.text.muted }
    : { ...s.stepNum, background: accent, color: color.surface.ink };
};

// The left rule on a row. DECORATION ONLY — the words (AWAITING / ✓ SEEN /
// STALE) are what actually carry the state; this is the third signal, never
// the first.
const leftRule = (state, stale) => {
  if (state === "acked") return `3px solid ${color.break.done}`;
  if (state === "stamped") return `3px solid ${stale ? color.border.default : color.accent.gold}`;
  return `3px solid transparent`;
};

// Every control on these screens. The transition is decoration and is skipped
// entirely under prefers-reduced-motion.
const buttonBase = (reduce) => ({
  width: "100%", minHeight: TOUCH_TARGET_MIN, minWidth: TOUCH_TARGET_MIN,
  display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center",
  gap: px(2), padding: `${px(6)} ${px(8)}`, borderRadius: px(radius.sm),
  border: `1px solid ${color.border.default}`, background: "transparent",
  color: color.text.primary, font: "inherit", textAlign: "left", cursor: "pointer",
  transition: reduce ? "none" : "background 120ms ease, border-color 120ms ease",
});

// ─────────────────────────────────────────────
//  PhaseRow — ONE phase, one <tr>, both modes.
//
//  Props (the wiring contract — do not widen without updating both pages):
//    { phaseId, stampedAt, ackAt, ackByName, ageMinutes, mode, onStamp,
//      onClear, onAck, busy }
//
//  ⚠️ ackByName is a resolved DISPLAY NAME. If the caller only has a uid it must
//  pass nothing: the row then renders "✓ SEEN 07:44" with no name, which is
//  true. Printing a uid where a colleague's name belongs is a worse failure
//  than printing no name at all.
// ─────────────────────────────────────────────
export function PhaseRow({
  phaseId,
  stampedAt = null,
  ackAt = null,
  ackByName = "",
  ageMinutes = null,
  mode = null,
  onStamp,
  onClear,
  onAck,
  busy = false,
}) {
  const label = phaseLabel(phaseId) || phaseId;
  const state = phaseState(stampedAt, ackAt);
  const stale = state !== "pending" && isPhaseStale(phaseId, ageMinutes);
  const reduce = prefersReducedMotion();

  const time = formatStampTime(stampedAt);
  const age = state === "pending" ? "" : formatAge(ageMinutes);

  // ⚠️ "not stamped", NOT "not started" (§12: missing ≠ not started). Regional
  // and MAC cases have no induction at all; an unstamped row means nobody
  // pressed the button, and the screen must not upgrade that into a clinical
  // claim about whether the case has begun.
  const stateWords = {
    pending: "not stamped",
    stamped: stale ? "stamped, awaiting acknowledgement, may no longer be current" : "stamped, awaiting acknowledgement",
    acked: "acknowledged",
  };
  const rowSummary = `${label}: ${state === "pending" ? "" : `${time}, `}${stateWords[state]}`;

  const isProvider = mode === "provider";
  const isSupervisor = mode === "supervisor";

  // ── Column 1: the phase. A button ONLY for the provider. ──
  // The four happen in order, every case, so they are numbered: this grid is a
  // CRNA reporting where in the case they are, and four identical boxes do not
  // say that. Decoration only — aria-hidden, because the accessible name
  // already reads "Stamp Induction" and "1 Induction" would read worse.
  const step = CASE_PHASES.indexOf(phaseId) + 1;

  // ⚠️ THE SECOND TAP CLEARS THE TIME, IT NO LONGER RE-STAMPS IT. Owner,
  // 2026-09-11: "If you click again the same button will clear that time." It
  // used to write `now` over the existing time, which meant the commonest
  // mistake — a fat finger on an already-stamped row — silently replaced a
  // correct induction time with the wrong one and there was no way to take a
  // stamp back at all. A correction is now two deliberate taps: clear, then
  // record. Neither destroys the old value (useCasePhases keeps `prior`).
  //
  // ⚠️ THE LABEL CHANGES WITH THE CONSEQUENCE. One button doing two different
  // things must never wear one word: "Record" stamps, "Clear" un-stamps, and
  // the accessible name says which so a screen-reader user is not told they are
  // about to record a time they are about to erase.
  const willClear = state !== "pending";
  const phaseCell = isProvider ? (
    <button
      type="button"
      data-testid={`phase-${phaseId}`}
      style={phaseButton(state, stale, reduce)}
      disabled={Boolean(busy)}
      aria-label={willClear ? `Clear ${label}, stamped ${time}` : `Stamp ${label}`}
      onClick={() => (willClear ? onClear && onClear(phaseId) : onStamp && onStamp(phaseId))}
    >
      <span aria-hidden="true" style={stepNumStyle(state, stale)}>
        {state === "pending" ? step : "✓"}
      </span>
      <span style={{ display: "flex", flexDirection: "column", gap: px(1), minWidth: 0 }}>
        <span style={s.phaseText}>{label}</span>
        {/* THE AFFORDANCE. Without it a pending box and a stamped box look
            like the same box to somebody using the screen for the first time,
            mid-case, with gloves on. */}
        <span style={s.phaseHint}>{willClear ? "Clear" : "Record"}</span>
      </span>
    </button>
  ) : (
    <span data-testid={`phase-${phaseId}`} style={s.phaseLabel}>
      <span aria-hidden="true" style={{ ...stepNumStyle(state, stale), marginRight: px(8) }}>
        {state === "pending" ? step : "✓"}
      </span>
      {label}
    </span>
  );

  // ── Column 3: the acknowledgement. A control ONLY for the supervisor, and
  // only while there is something to acknowledge. An already-acknowledged row
  // has no action left, and an unstamped row has nothing to receipt. ──
  let ackCell;
  if (state === "acked") {
    ackCell = (
      <span style={s.ackCell} data-testid={`ack-${phaseId}`}>
        <span style={{ ...s.ackPill, background: `${color.break.done}26`, color: color.break.done, border: `1px solid ${color.break.done}` }}>
          <span aria-hidden="true">✓</span>
          {`SEEN ${formatStampTime(ackAt)}`}
        </span>
        {ackByName ? <span style={s.ackName}>{`· ${ackByName}`}</span> : null}
      </span>
    );
  } else if (state === "stamped" && isSupervisor) {
    ackCell = (
      <button
        type="button"
        data-testid={`ack-${phaseId}`}
        style={{ ...buttonBase(reduce), borderColor: color.accent.gold }}
        disabled={Boolean(busy)}
        // Both visible strings appear in the accessible name (WCAG 2.5.3
        // label-in-name), so a voice-control user can say either word.
        aria-label={`AWAITING — Acknowledge ${label}, stamped ${time}`}
        onClick={() => onAck && onAck(phaseId)}
      >
        <span style={s.ackWord}>AWAITING</span>
        <span style={{ fontSize: 12, fontWeight: 700 }}>Acknowledge</span>
      </button>
    );
  } else if (state === "stamped") {
    ackCell = (
      <span style={s.ackCell} data-testid={`ack-${phaseId}`}>
        <span style={{ ...s.ackPill, background: `${color.accent.gold}1F`, color: color.accent.gold, border: `1px solid ${color.accent.gold}` }}>
          AWAITING
        </span>
      </span>
    );
  } else {
    ackCell = (
      <span style={s.ackCell} data-testid={`ack-${phaseId}`}>
        <span style={s.muted} aria-hidden="true">
          {EM_DASH}
        </span>
      </span>
    );
  }

  return (
    <tr
      data-testid={`phase-row-${phaseId}`}
      data-state={state}
      data-stale={String(stale)}
      aria-label={rowSummary}
    >
      <td style={{ ...s.td, borderLeft: leftRule(state, stale) }}>{phaseCell}</td>
      <td style={s.td}>
        <span style={s.timeCell}>
          {/* The TIME is what the supervisor is being told, so it is the
              largest thing in the row and set in tabular numerals — four times
              in a column that do not line up are four times you have to read
              twice. */}
          <span style={state === "pending" ? s.muted : s.timeBig}>{state === "pending" ? EM_DASH : time}</span>
          {age ? <span style={s.ageLine}>{age}</span> : null}
          {/* ⚠️ Past a plausible interval the row STOPS ASSERTING the phase.
              The word is the signal; the greyed rule is only its echo. */}
          {stale ? <span style={s.staleWord}>STALE</span> : null}
        </span>
      </td>
      <td style={s.td}>{ackCell}</td>
    </tr>
  );
}

// ─────────────────────────────────────────────
//  PhaseGrid — the four phases, in tap order, as one semantic table.
//
//  Reads the or_cases `phases` map (§4). Both pages use this, so the field
//  names live in exactly one place: a rename is one edit, not a hunt.
//  `ageMinutesFor(phaseId)` is bound by the caller — the supervisor page binds
//  its roomId in before handing it down.
// ─────────────────────────────────────────────
export function PhaseGrid({ phases, mode = null, onStamp, onClear, onAck, ageMinutesFor, busy = false, caption = "Case phases" }) {
  const ageOf = (phaseId) => {
    if (typeof ageMinutesFor !== "function") return null;
    const v = ageMinutesFor(phaseId);
    return Number.isFinite(v) ? v : null;
  };
  return (
    <table style={s.table}>
      <caption
        style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}
      >
        {caption}
      </caption>
      <colgroup>
        <col />
        <col style={{ width: 72 }} />
        <col style={{ width: 112 }} />
      </colgroup>
      <thead>
        <tr>
          <th scope="col" style={{ ...s.th, paddingLeft: px(11) }}>
            Phase
          </th>
          <th scope="col" style={s.th}>
            Time
          </th>
          <th scope="col" style={s.th}>
            Ack
          </th>
        </tr>
      </thead>
      <tbody>
        {CASE_PHASES.map((phaseId) => {
          const p = (phases && phases[phaseId]) || {};
          return (
            <PhaseRow
              key={phaseId}
              phaseId={phaseId}
              // `at` is the clinical time being asserted; `stampedAt` is when
              // the button was pressed (§4.3). The screen shows the clinical
              // time, falling back to the press only when there is no `at`.
              stampedAt={p.at ?? p.stampedAt ?? null}
              ackAt={p.ackAt ?? null}
              ackByName={p.ackByName || ""}
              ageMinutes={ageOf(phaseId)}
              mode={mode}
              onStamp={onStamp}
              onClear={onClear}
              onAck={onAck}
              busy={busy}
            />
          );
        })}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────
//  StatusHeader — room identity + the two case boundaries + New case.
//
//  variant "page": the provider screen's own header ("WOR4 ROOM STATUS", h1).
//  variant "card": one room inside the supervisor stack (room name, h2).
//  Interactivity is keyed on mode === "provider" ONLY, so a read-only render
//  (mode null) shows the same facts with nothing to press.
//
//  Time in / Time out are BOUNDARIES, not phases (§3): no acknowledgement, no
//  row in the grid. Their labels come from CASE_EVENT_META — never typed here,
//  because copied vocabulary is what silently diverges (§9).
// ─────────────────────────────────────────────
export function StatusHeader({
  roomName = "",
  surgeon = "",
  timeIn = null,
  timeOut = null,
  variant = "page",
  mode = null,
  onBoundary,
  onNewCase,
  busy = false,
}) {
  const reduce = prefersReducedMotion();
  const interactive = mode === "provider";
  const values = { time_in: timeIn, time_out: timeOut };
  const Heading = variant === "page" ? "h1" : "h2";

  return (
    <header style={{ display: "flex", flexDirection: "column", gap: px(space.xs), minWidth: 0 }}>
      <Heading
        data-testid="room-status-title"
        style={{
          margin: 0,
          minWidth: 0,
          fontSize: variant === "page" ? 20 : 17,
          fontWeight: 900,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: color.text.primary,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {variant === "page" ? `${roomName} ROOM STATUS` : roomName}
      </Heading>

      {/* The established gold ROLE: Name grammar (MobileRoomCard / occupantLine).
          The name is the only shrinkable thing in this line, so it owns the
          truncation and minWidth: 0 is load-bearing. */}
      <div
        data-testid="room-status-surgeon"
        style={{
          minWidth: 0,
          fontSize: 13,
          fontWeight: 700,
          color: color.accent.gold,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {`SURGEON: ${surgeon || EM_DASH}`}
      </div>

      <div style={{ display: "flex", gap: px(8), flexWrap: "wrap", marginTop: px(4), minWidth: 0 }}>
        {CASE_EVENTS.map((eventId) => {
          const label = CASE_EVENT_META[eventId].label;
          const time = formatStampTime(values[eventId]);
          const stamped = time !== EM_DASH;
          if (!interactive) {
            return (
              <span key={eventId} data-testid={`boundary-${eventId}`} style={{ fontSize: 13, color: color.text.muted, minWidth: 0 }}>
                {`${label} `}
                <b style={{ color: stamped ? color.text.primary : color.text.muted }}>{time}</b>
              </span>
            );
          }
          return (
            <button
              key={eventId}
              type="button"
              data-testid={`boundary-${eventId}`}
              disabled={Boolean(busy)}
              onClick={() => onBoundary && onBoundary(eventId)}
              aria-label={stamped ? `Re-stamp ${label}, stamped ${time}` : `Stamp ${label}`}
              style={{
                ...buttonBase(reduce),
                width: "auto",
                flex: "1 1 40%",
                // ≥16px or iOS WKWebView zooms the page the moment a control is
                // focused, and the whole app then pans side to side.
                fontSize: INPUT_FONT_SIZE,
                borderColor: stamped ? color.accent.gold : color.border.default,
              }}
            >
              <span style={{ fontSize: 12, color: color.text.muted, fontWeight: 700 }}>{label}</span>
              <span style={{ fontSize: INPUT_FONT_SIZE, fontWeight: 800 }}>{time}</span>
            </button>
          );
        })}
      </div>

      {interactive ? (
        <button
          type="button"
          data-testid="new-case"
          disabled={Boolean(busy)}
          onClick={() => onNewCase && onNewCase()}
          aria-label="New case"
          style={{
            ...buttonBase(reduce),
            alignItems: "center",
            marginTop: px(6),
            fontSize: INPUT_FONT_SIZE,
            fontWeight: 800,
            background: color.accent.primary,
            borderColor: color.accent.primary,
          }}
        >
          <span>New case</span>
        </button>
      ) : null}
    </header>
  );
}

export default PhaseRow;
